import {
  type ChatMessage,
  type Content,
  type MediaRef,
  mediaSupport,
  type RhemaClient,
  safeImageUrl,
} from "@rhema/client";
import { cn, focusRing, PlayIcon } from "@rhema/ui";
import {
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SyntheticEvent,
  useState,
} from "react";
import { THUMB_EDGE } from "../media/image";
import { formatDuration } from "../media/prepare";
import { useInView } from "../media/useInView";
import { useMediaUrl } from "../media/useMedia";

// Photos and videos inside a bubble, alone or as an album, with what shows while they send.

type MediaContent = Extract<Content, { type: "media" }>;

export const UPDATE_TO_OPEN = "Update Rhema to open this file.";

function pixelRatio(): number {
  return typeof window === "undefined" ? 1 : Math.min(3, window.devicePixelRatio || 1);
}

/**
 * A photo, or a video's first frame, painted in three steps so nothing jumps: the tiny blurred
 * placeholder at once, then the sharp 320 px preview from inside the message, then the full photo
 * once it comes near the screen. Where the preview is already sharp enough for the space it fills,
 * as in most album tiles, the full photo is never fetched. `poster` never fetches the file at all.
 */
export function MediaImage({
  media,
  local,
  width,
  alt = "",
  poster = false,
}: {
  media: MediaRef;
  /** The file this device is sending, shown in place of a download. */
  local?: Blob;
  /** About how wide it shows, in CSS pixels. */
  width: number;
  alt?: string;
  poster?: boolean;
}) {
  // Only embedded images are shown. A web address would tell its server when the chat was opened.
  const placeholder = safeImageUrl(media.placeholder);
  // Messages from before placeholders carry a 32 px thumbnail, which is only good blurred.
  const sharp = placeholder ? safeImageUrl(media.thumbnail) : undefined;
  const blur = placeholder ?? safeImageUrl(media.thumbnail);
  const update = mediaSupport(media) === "update";
  const needFull = !poster && !update && (!sharp || width * pixelRatio() > THUMB_EDGE);
  const { ref, seen } = useInView<HTMLSpanElement>(needFull);
  const want = needFull && seen;
  const { url } = useMediaUrl(want ? media : undefined, want ? local : undefined, want);
  const [shown, setShown] = useState(false);
  return (
    <span ref={ref} className="absolute inset-0 block overflow-hidden bg-surface-header">
      {blur && (
        <img
          src={blur}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-110 object-cover blur-md"
        />
      )}
      {sharp && (
        <img
          src={sharp}
          alt={url ? "" : alt}
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {url && (
        <img
          src={url}
          alt={alt}
          decoding="async"
          data-full=""
          onLoad={() => setShown(true)}
          className={cn(
            "absolute inset-0 h-full w-full object-cover transition-opacity duration-200",
            shown || !blur ? "opacity-100" : "opacity-0",
          )}
        />
      )}
    </span>
  );
}

/** A file you are sending: how far its upload is, or "Next" while it waits its turn. */
export function ProgressRing({ progress, waiting }: { progress: number; waiting: boolean }) {
  const percent = Math.round(progress * 100);
  const r = 18;
  const length = 2 * Math.PI * r;
  return (
    <span
      role="progressbar"
      aria-label="Uploading"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-valuetext={waiting ? "Waiting to upload" : `${percent}%`}
      className="relative flex h-11 w-11 items-center justify-center"
    >
      <svg aria-hidden="true" viewBox="0 0 44 44" className="absolute inset-0 h-11 w-11 -rotate-90">
        <circle cx="22" cy="22" r="21" fill="var(--color-linen)" fillOpacity="0.94" />
        <circle cx="22" cy="22" r={r} fill="none" stroke="var(--color-line-strong)" strokeWidth="3" />
        <circle
          cx="22"
          cy="22"
          r={r}
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={length}
          strokeDashoffset={length * (1 - (waiting ? 0 : progress))}
          className="transition-[stroke-dashoffset] duration-200"
        />
      </svg>
      <span aria-hidden="true" className="relative text-[11px] font-semibold text-ink">
        {waiting ? "Next" : `${percent}%`}
      </span>
    </span>
  );
}

/** Plain words for why a file was not sent. */
export function notSentText(client: Pick<RhemaClient, "uploads">, m: ChatMessage): string {
  const item = m.clientId ? client.uploads.get(m.clientId) : undefined;
  if (item?.errorCode === "missing_file") return "Not sent. The file is no longer on this device.";
  if (item?.errorCode === "too_large") return "Not sent. Files can be up to 2 GB.";
  if (item?.errorCode && item.error) return `Not sent. ${item.error}`;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "Not sent. You are offline.";
  const error = item?.error ?? "";
  if (!error || /interrupt|network|fetch|timed out|Upload failed \(5|did not finish/i.test(error))
    return "Not sent. Network lost.";
  return `Not sent. ${error}`;
}

/** Whether the queue is still waiting to start this file. */
export function isWaiting(client: Pick<RhemaClient, "uploads">, m: ChatMessage): boolean {
  if (!m.clientId) return false;
  return client.uploads.get(m.clientId)?.status === "queued";
}

export function aspect(media: MediaRef): number {
  if (!media.width || !media.height) return 4 / 3;
  return Math.min(1.8, Math.max(0.6, media.width / media.height));
}

function failedHere(m: ChatMessage, meId: string | undefined): boolean {
  return m.seq === 0 && m.status === "failed" && m.senderId === meId;
}

/** Props from the message menu, kept on the tile they belong to so an album's parent does not also act. */
export type TileProps = Partial<HTMLAttributes<HTMLElement>>;

function isolate(props: TileProps | undefined): TileProps {
  if (!props) return {};
  const out: TileProps = {};
  for (const [key, value] of Object.entries(props) as [keyof TileProps, unknown][]) {
    if (key === "tabIndex" || key === "role" || key.startsWith("aria-")) continue;
    if (typeof value === "function" && key.startsWith("on")) {
      (out as Record<string, unknown>)[key] = (e: SyntheticEvent) => {
        // Enter on a tile opens it. The menu has its own keys.
        if (key === "onKeyDown" && (e as KeyboardEvent).key === "Enter") return;
        (value as (e: SyntheticEvent) => void)(e);
        // Capture handlers run on the way down, and stopping there would also stop the tile's own click.
        if (!key.endsWith("Capture")) e.stopPropagation();
      };
    } else {
      (out as Record<string, unknown>)[key] = value;
    }
  }
  return out;
}

type Layout = { columns: string; rows: string; ratio: number; shown: number };

/** The album's grid, as WhatsApp and Telegram lay them out: side by side, one over two, or two by two. */
export function albumLayout(count: number): Layout {
  if (count <= 1) return { columns: "1fr", rows: "1fr", ratio: 4 / 3, shown: 1 };
  if (count === 2) return { columns: "1fr 1fr", rows: "1fr", ratio: 2 / 1.3, shown: 2 };
  if (count === 3) return { columns: "1fr 1fr", rows: "1.25fr 1fr", ratio: 2 / 2.25, shown: 3 };
  return { columns: "1fr 1fr", rows: "1fr 1fr", ratio: 1, shown: 4 };
}

export const ALBUM_WIDTH = 300;

export function AlbumGrid({
  items,
  meId,
  client,
  onOpen,
  onRetry,
  tileProps,
}: {
  items: ChatMessage[];
  meId: string | undefined;
  client: Pick<RhemaClient, "uploads">;
  onOpen: (m: ChatMessage) => void;
  onRetry?: (m: ChatMessage) => void;
  tileProps?: (m: ChatMessage) => TileProps | undefined;
}) {
  const layout = albumLayout(items.length);
  const shown = items.slice(0, layout.shown);
  const hidden = items.length - shown.length;
  const inner = ALBUM_WIDTH - 8;
  return (
    // biome-ignore lint/a11y/useSemanticElements: a fieldset is for form controls, and this is a grid of photos
    <div
      role="group"
      aria-label={`Album of ${items.length}`}
      className="grid gap-[3px]"
      style={{
        gridTemplateColumns: layout.columns,
        gridTemplateRows: layout.rows,
        aspectRatio: layout.ratio,
      }}
    >
      {shown.map((m, i) => {
        const c = m.content as MediaContent;
        const wide = items.length === 3 && i === 0;
        const failed = failedHere(m, meId);
        const busy = m.progress !== undefined && !failed;
        const more = i === shown.length - 1 && hidden > 0 ? hidden : 0;
        const video = c.kind === "video";
        const label = more
          ? `Open album, ${hidden} more`
          : video
            ? `Play video ${i + 1} of ${items.length}, ${formatDuration(c.media.duration)}`
            : `Open photo ${i + 1} of ${items.length}${c.caption ? `: ${c.caption}` : ""}`;
        return (
          <div
            key={m.id}
            className={cn("relative min-h-0 min-w-0 overflow-hidden rounded-[8px]", wide && "col-span-2")}
          >
            <button
              type="button"
              aria-label={label}
              onClick={() => onOpen(m)}
              {...isolate(tileProps?.(m))}
              className={cn(
                "absolute inset-0 block cursor-zoom-in border-0 bg-transparent p-0",
                video && "cursor-pointer",
                focusRing,
              )}
            >
              <MediaImage
                media={c.media}
                local={m.localFile}
                width={wide ? inner : inner / 2}
                poster={video}
              />
              {video && !busy && !failed && !more && (
                <>
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue text-white">
                      <PlayIcon size={15} />
                    </span>
                  </span>
                  <span className="absolute bottom-1.5 left-1.5 rounded-full bg-linen px-2 py-0.5 text-[11px] font-semibold text-ink">
                    {formatDuration(c.media.duration)}
                  </span>
                </>
              )}
              {more > 0 && (
                <span className="absolute inset-0 flex items-center justify-center bg-linen/70 font-serif text-[28px] font-semibold text-ink">
                  +{more}
                </span>
              )}
            </button>
            {busy && (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <ProgressRing progress={m.progress ?? 0} waiting={isWaiting(client, m)} />
              </span>
            )}
            {failed && (
              <span className="absolute inset-0 flex items-center justify-center bg-linen/60">
                {onRetry && (
                  <button
                    type="button"
                    aria-label={`Retry ${video ? "video" : "photo"} ${i + 1}`}
                    onClick={() => onRetry(m)}
                    className={cn(
                      "cursor-pointer rounded-lg border-0 bg-blue-tint px-2.5 py-1.5 text-[13px] font-semibold text-blue",
                      focusRing,
                    )}
                  >
                    Retry
                  </button>
                )}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The line under photos you are sending: "Sending 3 of 4" with Cancel, which stops everything not
 * yet uploaded, or which of them were not sent, with Retry.
 */
export function SendingLine({
  items,
  meId,
  client,
  onCancel,
  onRetry,
}: {
  items: ChatMessage[];
  meId: string | undefined;
  client: Pick<RhemaClient, "uploads">;
  onCancel?: (list: ChatMessage[]) => void;
  onRetry?: (list: ChatMessage[]) => void;
}): ReactNode {
  const failed = items.filter((m) => failedHere(m, meId));
  const busy = items.filter((m) => m.progress !== undefined && !failedHere(m, meId));
  if (failed.length) {
    const text = notSentText(client, failed[0] as ChatMessage);
    const line =
      items.length > 1 && failed.length < items.length
        ? `${failed.length} of ${items.length} not sent. ${text.replace(/^Not sent\. /, "")}`
        : text;
    return (
      <div className="flex items-center justify-between gap-3 px-1.5 pt-1.5 text-[13px]">
        <span role="status" className="text-scarlet">
          {line}
        </span>
        {onRetry && (
          <button
            type="button"
            onClick={() => onRetry(failed)}
            className={cn(
              "flex-none cursor-pointer rounded-lg border-0 bg-blue-tint px-2.5 py-1.5 text-[13px] font-semibold text-blue",
              focusRing,
            )}
          >
            Retry
          </button>
        )}
      </div>
    );
  }
  if (!busy.length) return null;
  const done = items.length - busy.length;
  const text =
    items.length > 1
      ? `Sending ${Math.min(items.length, done + 1)} of ${items.length}`
      : isWaiting(client, busy[0] as ChatMessage)
        ? "Waiting to send"
        : "Sending";
  return (
    <div className="flex items-center justify-between gap-3 px-1.5 pt-1.5 text-[13px] text-ink-secondary">
      <span role="status">{text}</span>
      {onCancel && (
        <button
          type="button"
          aria-label={items.length > 1 ? "Cancel sending the rest of the album" : "Cancel upload"}
          onClick={(e) => {
            e.stopPropagation();
            onCancel(busy);
          }}
          className={cn(
            "flex-none cursor-pointer border-0 bg-transparent p-0 text-[13px] font-semibold text-scarlet",
            focusRing,
          )}
        >
          Cancel
        </button>
      )}
    </div>
  );
}
